import { Button, List, Stack, Text } from "@chakra-ui/react";
import { Fragment, useState } from "react";
import MultipleInput from "@/ui/inputs/MultipleInput";
import { useAuth } from "@/shared/session";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";
import { Radio, RadioGroup } from "@/ui/chakra/radio";
import { Checkbox } from "@/ui/chakra/checkbox";
import { Hint } from "@/ui/Hint";
import { notify } from "@/ui/chakra/notify";
import {
  useBulkValidateVouchers,
  voucherApi,
  type Voucher,
} from "@/shared/core-vouchers";
import { trackEvent } from "@/utils/firebase/analytics";

export type BulkVoucherMode = "validate" | "send";

interface BulkVoucherDialogProps {
  mode: BulkVoucherMode | null;
  vouchers: Voucher[];
  onClose: () => void;
  onDone: () => void;
}

enum SendTo {
  Me = "me",
  Other = "other",
}

const MAX_EMAILS = 5;

const isValidated = (voucher: Voucher) =>
  voucher.voucherState?.name === "Validado";

export const BulkVoucherDialog = ({
  mode,
  vouchers,
  onClose,
  onDone,
}: BulkVoucherDialogProps) => {
  const { user } = useAuth();
  const [sendTo, setSendTo] = useState<SendTo>(SendTo.Me);
  const [emails, setEmails] = useState<string[]>([]);
  const [withCopy, setWithCopy] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const { bulkValidateVouchers } = useBulkValidateVouchers();

  const isValidate = mode === "validate";
  const pending = vouchers.filter((voucher) => !isValidated(voucher));
  const alreadyValidated = vouchers.length - pending.length;
  const targets = isValidate ? pending : vouchers;

  const handleClose = () => {
    setSendTo(SendTo.Me);
    setEmails([]);
    setWithCopy(false);
    setIsSending(false);
    onClose();
  };

  const recipients = () => {
    if (sendTo === SendTo.Me) return [];
    return withCopy && user ? [...emails, user.email] : emails;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSending || !targets.length) return;
    if (sendTo === SendTo.Other && emails.length === 0) {
      notify.warning("Ingresa al menos un correo");
      return;
    }
    if (emails.length > MAX_EMAILS) {
      notify.warning(`Puedes ingresar hasta ${MAX_EMAILS} correos`);
      return;
    }

    const ids = targets.map((voucher) => voucher.id);
    setIsSending(true);
    try {
      if (isValidate) {
        const result = await bulkValidateVouchers({
          ids,
          emails: recipients(),
        });
        trackEvent("voucher_bulk_action", {
          action: "validate",
          count: ids.length,
        });
        if (result.failed.length) {
          const codes = result.failed
            .map((f) => f.code ?? f.id.slice(0, 8))
            .join(", ");
          notify.warning(
            `Se validaron ${result.validated.length} de ${ids.length} vales`,
            `No se pudieron validar: ${codes}`,
          );
        } else {
          notify.success(
            result.validated.length === 1
              ? "Vale validado"
              : `${result.validated.length} vales validados`,
          );
        }
      } else {
        const to = sendTo === SendTo.Me ? undefined : recipients();
        await voucherApi.sendEmailVouchers(ids, to);
        trackEvent("voucher_bulk_action", {
          action: "send_email",
          count: ids.length,
        });
        notify.success(
          ids.length === 1 ? "Vale enviado" : `${ids.length} vales enviados`,
          "Se envió un correo con el PDF de cada vale adjunto.",
        );
      }
      onDone();
      handleClose();
    } catch (error) {
      notify.error(
        isValidate
          ? "No se pudieron validar los vales"
          : "No se pudo enviar el correo",
        error,
      );
      setIsSending(false);
    }
  };

  return (
    <DialogRoot isOpen={!!mode} onClose={handleClose} isCentered size="md">
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isValidate
                ? `Validar ${targets.length} ${targets.length === 1 ? "vale" : "vales"}`
                : `Enviar ${targets.length} ${targets.length === 1 ? "vale" : "vales"} por correo`}
            </DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <Stack gap={4}>
              {isValidate && alreadyValidated > 0 && (
                <Hint>
                  {alreadyValidated === 1
                    ? "1 vale seleccionado ya está validado y se omitirá."
                    : `${alreadyValidated} vales seleccionados ya están validados y se omitirán.`}
                </Hint>
              )}
              {isValidate && targets.length === 0 ? (
                <Text color="fg.muted">
                  Todos los vales seleccionados ya están validados.
                </Text>
              ) : (
                <Fragment>
                  <Text fontSize="sm" color="fg.muted">
                    {isValidate
                      ? "Cada vale se envía por correo a su solicitante y a los destinatarios que elijas."
                      : "Se enviará un solo correo con el PDF de cada vale adjunto."}
                  </Text>
                  <List.Root
                    fontSize="sm"
                    maxH="120px"
                    overflowY="auto"
                    ps={5}
                    color="fg.muted"
                  >
                    {targets.map((voucher) => (
                      <List.Item key={voucher.id}>
                        <Text as="span" fontWeight="medium" color="fg">
                          {voucher.code}
                        </Text>
                        {voucher.productApplicant?.name
                          ? ` · ${voucher.productApplicant.name}`
                          : ""}
                      </List.Item>
                    ))}
                  </List.Root>
                  <RadioGroup
                    colorPalette="brand"
                    value={sendTo}
                    onValueChange={(e) => {
                      setSendTo(e.value as SendTo);
                      setEmails([]);
                    }}
                  >
                    <Stack direction="column" gap={3}>
                      <Radio value={SendTo.Me}>Enviar a mi correo</Radio>
                      <Radio value={SendTo.Other}>
                        Enviar a otro(s) correo(s)
                      </Radio>
                    </Stack>
                  </RadioGroup>
                  {sendTo === SendTo.Other && (
                    <Fragment>
                      <MultipleInput
                        values={emails}
                        setValues={setEmails}
                        placeholder="Ingrese correos y presione Enter o coma"
                      />
                      <Checkbox
                        colorPalette="brand"
                        checked={withCopy}
                        onCheckedChange={(e) => setWithCopy(e.checked === true)}
                      >
                        Deseo recibir una copia
                      </Checkbox>
                    </Fragment>
                  )}
                </Fragment>
              )}
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={handleClose} type="button">
              Cancelar
            </Button>
            <Button
              colorPalette="brand"
              type="submit"
              loading={isSending}
              disabled={!targets.length}
            >
              {isValidate ? "Validar y enviar" : "Enviar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
};
